import { formatSiteDate } from "@/lib/site-time";
/**
 * Intern dashboard voor Core Web Vitals bij echte bezoekers.
 *
 * Achter de adminbeveiliging. Bij het verwachte volume staat de trend standaard
 * op maandbasis; dag- en weekniveau blijven beschikbaar als inzoom.
 */
/* i18n-ignore: interne adminpagina, alleen NL */
import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import {
  getVitalsDashboard,
  getVitalsLive,
  getVitalsYearOverYear,
  rollupVitalsMonthly,
} from "@/lib/vitals-admin.functions";
import {
  CORE_METRICS,
  DISPLAY_THRESHOLDS,
  METRIC_INFO,
  METRIC_NAMES,
  THRESHOLDS,
  displayRating,
  formatMetric,
  type DisplayRating,
  type MetricName,
} from "@/lib/web-vitals";
import { Button } from "@/components/ui/button";
import { AdminNav } from "@/components/admin/AdminNav";
import { Badge } from "@/components/ui/badge";

export const Route = createFileRoute("/_authenticated/admin/vitals")({
  staticData: { sitemap: false },
  head: () => ({
    meta: [
      { title: "Web Vitals | Beheer Gopro Projects" },
      { name: "description", content: "Core Web Vitals bij echte bezoekers, per route en apparaat." },
      { name: "robots", content: "noindex, nofollow" },
      { property: "og:title", content: "Web Vitals | Beheer Gopro Projects" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: VitalsPage,
});

type Device = "all" | "mobile" | "desktop";
type Granularity = "day" | "week" | "month";

const RATING_CLASS: Record<DisplayRating, string> = {
  excellent: "bg-sky-100 text-sky-800 border-sky-200",
  "very-good": "bg-amber-100 text-amber-900 border-amber-200",
  good: "bg-emerald-100 text-emerald-800 border-emerald-200",
  "needs-improvement": "bg-orange-100 text-orange-800 border-orange-200",
  poor: "bg-destructive/10 text-destructive border-destructive/30",
};

const RATING_LABEL: Record<DisplayRating, string> = {
  excellent: "uitmuntend",
  "very-good": "heel goed",
  good: "goed",
  "needs-improvement": "matig",
  poor: "slecht",
};

function thresholdText(metric: MetricName): string {
  const t = THRESHOLDS[metric];
  const d = DISPLAY_THRESHOLDS[metric];
  return `uitmuntend ≤ ${formatMetric(metric, d.excellent)} · heel goed ≤ ${formatMetric(metric, d.veryGood)} · goed ≤ ${formatMetric(metric, t.good)} · slecht > ${formatMetric(metric, t.poor)}`;
}

function Panel({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-xl border border-border bg-card p-4 md:p-6">
      <header className="mb-4">
        <h2 className="text-base font-semibold tracking-tight">{title}</h2>
        {subtitle ? <p className="text-xs text-muted-foreground">{subtitle}</p> : null}
      </header>
      {children}
    </section>
  );
}

function LowSampleNote({ samples, minimum }: { samples: number; minimum: number }) {
  if (samples >= minimum) return null;
  return (
    <p className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
      Te weinig metingen ({samples} van minstens {minimum}) voor een betrouwbare p75. Lees deze
      cijfers als indicatie, niet als trend.
    </p>
  );
}

function VitalsPage() {
  const [device, setDevice] = useState<Device>("all");
  const [days, setDays] = useState(28);
  const [granularity, setGranularity] = useState<Granularity>("month");
  const [yoyMetric, setYoyMetric] = useState<MetricName>("LCP");
  const [yoyYears, setYoyYears] = useState(2);
  const [liveMinutes, setLiveMinutes] = useState(60);

  const dashboardFn = useServerFn(getVitalsDashboard);
  const liveFn = useServerFn(getVitalsLive);
  const yoyFn = useServerFn(getVitalsYearOverYear);
  const rollupFn = useServerFn(rollupVitalsMonthly);
  const queryClient = useQueryClient();

  const dash = useQuery({
    queryKey: ["vitals", days, device, granularity],
    queryFn: () => dashboardFn({ data: { days, device, granularity } }),
  });

  const live = useQuery({
    queryKey: ["vitals-live", liveMinutes],
    queryFn: () => liveFn({ data: { minutes: liveMinutes, limit: 60 } }),
    refetchInterval: 15000,
    refetchOnWindowFocus: true,
  });

  const yoy = useQuery({
    queryKey: ["vitals-yoy", yoyMetric, device, yoyYears],
    queryFn: () => yoyFn({ data: { metric: yoyMetric, device, years: yoyYears } }),
  });

  const rollup = useMutation({
    mutationFn: () => rollupFn({ data: { months: 2 } }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["vitals-yoy"] });
    },
  });

  const data = dash.data;
  const minimum = data?.minReliableSamples ?? 50;

  const distributionData = useMemo(
    () =>
      (data?.distribution ?? []).map((row) => ({
        metric: row.metric,
        goed: row.good,
        matig: row.needsImprovement,
        slecht: row.poor,
      })),
    [data],
  );

  return (
    <main className="mx-auto w-full max-w-6xl px-4 py-10 md:px-8">
      <AdminNav />

      <div className="mb-6 mt-4 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight md:text-3xl">Web Vitals</h1>
          <p className="text-sm text-muted-foreground">
            Gemeten bij echte bezoekers, zonder cookies. {data?.totalSamples ?? 0} metingen in de
            gekozen periode.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {(["all", "mobile", "desktop"] as Device[]).map((d) => (
            <Button
              key={d}
              size="sm"
              variant={device === d ? "default" : "outline"}
              onClick={() => setDevice(d)}
            >
              {d === "all" ? "Alle apparaten" : d === "mobile" ? "Mobiel" : "Desktop"}
            </Button>
          ))}
          {[28, 90, 365].map((d) => (
            <Button
              key={d}
              size="sm"
              variant={days === d ? "default" : "outline"}
              onClick={() => setDays(d)}
            >
              {d} dagen
            </Button>
          ))}
        </div>
      </div>

      {dash.isLoading ? <p className="text-sm text-muted-foreground">Bezig met laden…</p> : null}
      {dash.error ? (
        <p className="text-sm text-destructive">Kon de metingen niet laden: {String(dash.error)}</p>
      ) : null}

      <div className="grid gap-6">
        <Panel
          title="Live — echte bezoekers"
          subtitle="Ruwe metingen zoals ze binnenkomen. Ververst automatisch elke 15 seconden."
        >
          <div className="mb-4 flex flex-wrap items-center gap-2">
            {[15, 60, 360, 1440].map((m) => (
              <Button
                key={m}
                size="sm"
                variant={liveMinutes === m ? "default" : "outline"}
                onClick={() => setLiveMinutes(m)}
              >
                {m < 60 ? `${m} min` : m < 1440 ? `${m / 60} uur` : "24 uur"}
              </Button>
            ))}
            <span className="text-xs text-muted-foreground">
              {live.data ? `${live.data.totalSamples} metingen in dit venster` : "…"}
              {live.isFetching ? " · verversen…" : ""}
            </span>
          </div>

          <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-5">
            {(live.data?.latest ?? []).map((s) => (
              <div key={s.metric} className="rounded-lg border border-border p-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-muted-foreground">{s.metric}</span>
                  {s.value !== null ? (
                    <Badge
                      variant="outline"
                      className={RATING_CLASS[displayRating(s.metric, s.value)]}
                    >
                      {RATING_LABEL[displayRating(s.metric, s.value)]}
                    </Badge>
                  ) : null}
                </div>
                <p className="mt-1 font-display text-2xl">{formatMetric(s.metric, s.value)}</p>
                <p className="text-xs text-muted-foreground">
                  laatste meting · p75 {formatMetric(s.metric, s.p75)} · {s.samples}×
                </p>
              </div>
            ))}
          </div>

          {live.data && live.data.recent.length > 0 ? (
            <div className="mt-4 overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead className="text-muted-foreground">
                  <tr>
                    <th className="py-1 pr-3 font-medium">Tijd</th>
                    <th className="py-1 pr-3 font-medium">Metriek</th>
                    <th className="py-1 pr-3 font-medium">Waarde</th>
                    <th className="py-1 pr-3 font-medium">Pagina</th>
                    <th className="py-1 pr-3 font-medium">Apparaat</th>
                    <th className="py-1 font-medium">Verbinding</th>
                  </tr>
                </thead>
                <tbody>
                  {live.data.recent.slice(0, 25).map((r, i) => (
                    <tr key={`${r.createdAt}-${r.metric}-${i}`} className="border-t border-border">
                      <td className="py-1 pr-3 tabular-nums">
                        {formatSiteDate(r.createdAt, "nl-BE", { timeStyle: "medium" })}
                      </td>
                      <td className="py-1 pr-3">{r.metric}</td>
                      <td className="py-1 pr-3 tabular-nums">
                        <span
                          className={`rounded px-1.5 py-0.5 ${RATING_CLASS[displayRating(r.metric, r.value)]}`}
                        >
                          {formatMetric(r.metric, r.value)}
                        </span>
                      </td>
                      <td className="py-1 pr-3 text-muted-foreground">{r.route}</td>
                      <td className="py-1 pr-3 text-muted-foreground">{r.device}</td>
                      <td className="py-1 text-muted-foreground">{r.connection ?? "–"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="mt-4 text-xs text-muted-foreground">
              Nog geen metingen in dit venster. Metingen komen alleen van de gepubliceerde site — in
              de preview wordt bewust niets weggeschreven.
            </p>
          )}
        </Panel>

        <Panel
          title="Status (p75)"
          subtitle={`Laatste ${days} dagen · ${device === "all" ? "alle apparaten samen" : device === "mobile" ? "mobiel" : "desktop"}`}
        >
          <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-5">
            {(data?.summaries ?? []).map((s) => (
              <div key={s.metric} className="rounded-lg border border-border p-3">
                <div className="flex items-center justify-between">
                  <span
                    className="text-xs font-medium text-muted-foreground"
                    title={METRIC_INFO[s.metric].fullName}
                  >
                    {s.metric}
                  </span>
                  {s.rating ? (
                    <Badge variant="outline" className={RATING_CLASS[s.rating]}>
                      {RATING_LABEL[s.rating]}
                    </Badge>
                  ) : null}
                </div>
                <p className="text-[11px] text-muted-foreground">
                  {METRIC_INFO[s.metric].fullName}
                </p>
                <p className="mt-1 font-display text-2xl">{formatMetric(s.metric, s.p75)}</p>
                <p className="text-xs text-muted-foreground">
                  mediaan {formatMetric(s.metric, s.p50)} · {s.samples} metingen
                  {s.reliable ? "" : " (te weinig)"}
                </p>
                <p className="text-[11px] text-muted-foreground">{thresholdText(s.metric)}</p>
              </div>
            ))}
          </div>
          {device === "all" ? (
            <p className="mt-3 text-xs text-muted-foreground">
              Tip: bekijk mobiel en desktop apart — samengevoegd verbergen ze elkaars problemen.
            </p>
          ) : null}
        </Panel>

        <Panel
          title="Wat meten we?"
          subtitle="Officiële Google-metrieken met hun drempels. p75 = 75% van de bezoekers had het minstens zo goed."
        >
          <ul className="grid gap-3 text-sm md:grid-cols-2 lg:grid-cols-3">
            {METRIC_NAMES.map((m) => (
              <li key={m} className="rounded-lg border border-border p-3">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium">
                    {m}{" "}
                    <span className="font-normal text-muted-foreground">
                      — {METRIC_INFO[m].fullName}
                    </span>
                  </span>
                  {METRIC_INFO[m].core ? (
                    <Badge
                      variant="outline"
                      className="bg-emerald-100 text-emerald-800 border-emerald-200"
                    >
                      Core
                    </Badge>
                  ) : (
                    <Badge variant="outline">diagnostisch</Badge>
                  )}
                </div>
                <p className="mt-1 text-xs text-muted-foreground">{METRIC_INFO[m].description}</p>
                <p className="mt-1 text-[11px] text-muted-foreground">{thresholdText(m)}</p>
              </li>
            ))}
          </ul>
        </Panel>

        <Panel title="Verdeling per metriek" subtitle="Aandeel goed / matig / slecht in procent">
          <div className="h-72 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={distributionData} layout="vertical" stackOffset="expand">
                <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                <XAxis type="number" tickFormatter={(v: number) => `${Math.round(v * 100)}%`} />
                <YAxis type="category" dataKey="metric" width={56} />
                <Tooltip formatter={(v: number) => `${v}%`} />
                <Legend />
                <Bar dataKey="goed" stackId="a" fill="var(--chart-1)" />
                <Bar dataKey="matig" stackId="a" fill="var(--chart-4)" />
                <Bar dataKey="slecht" stackId="a" fill="var(--chart-5)" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Panel>

        <Panel
          title="Trend p75"
          subtitle="Standaard per maand: bij dit verkeersvolume is dat het fijnste niveau met betekenisvolle cijfers."
        >
          <div className="mb-3 flex gap-2">
            {(["month", "week", "day"] as Granularity[]).map((g) => (
              <Button
                key={g}
                size="sm"
                variant={granularity === g ? "default" : "outline"}
                onClick={() => setGranularity(g)}
              >
                {g === "month" ? "Per maand" : g === "week" ? "Per week" : "Per dag"}
              </Button>
            ))}
          </div>
          {granularity !== "month" ? (
            <p className="mb-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
              Inzoom op {granularity === "week" ? "week" : "dag"}niveau: de meeste punten steunen op
              enkele metingen en zijn dus ruis, geen trend.
            </p>
          ) : null}
          <div className="h-80 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data?.trend ?? []}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                <XAxis dataKey="bucket" />
                <YAxis yAxisId="ms" />
                <YAxis yAxisId="cls" orientation="right" />
                <Tooltip />
                <Legend />
                <Line yAxisId="ms" type="monotone" dataKey="LCP" stroke="var(--chart-1)" dot={false} />
                <Line yAxisId="ms" type="monotone" dataKey="INP" stroke="var(--chart-2)" dot={false} />
                <Line yAxisId="ms" type="monotone" dataKey="FCP" stroke="var(--chart-3)" dot={false} />
                <Line yAxisId="ms" type="monotone" dataKey="TTFB" stroke="var(--chart-4)" dot={false} />
                <Line yAxisId="cls" type="monotone" dataKey="CLS" stroke="var(--chart-5)" dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Panel>

        <Panel title="Per pagina" subtitle="p75 per route, gesorteerd op aantal metingen">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px] text-sm">
              <thead className="text-left text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="py-2">Route</th>
                  {METRIC_NAMES.map((m) => (
                    <th key={m} className="py-2" title={METRIC_INFO[m].fullName}>
                      {m}
                    </th>
                  ))}
                  <th className="py-2">Metingen</th>
                </tr>
              </thead>
              <tbody>
                {(data?.routes ?? []).map((row) => (
                  <tr key={row.route} className="border-t border-border">
                    <td className="py-2 font-mono text-xs">{row.route}</td>
                    {METRIC_NAMES.map((m) => (
                      <td key={m} className="py-2">
                        {formatMetric(m, row[m] ?? null)}
                      </td>
                    ))}
                    <td className="py-2 text-muted-foreground">{row.samples}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <LowSampleNote samples={data?.totalSamples ?? 0} minimum={minimum} />
        </Panel>

        <Panel title="Jaarvergelijking" subtitle="p75 per maand, vergeleken met vorige jaren">
          <div className="mb-3 flex flex-wrap gap-2">
            {METRIC_NAMES.map((m) => (
              <Button
                key={m}
                size="sm"
                variant={yoyMetric === m ? "default" : "outline"}
                onClick={() => setYoyMetric(m)}
              >
                {m}
              </Button>
            ))}
            {[1, 2, 3].map((y) => (
              <Button
                key={y}
                size="sm"
                variant={yoyYears === y ? "default" : "outline"}
                onClick={() => setYoyYears(y)}
              >
                {y} jaar
              </Button>
            ))}
            <Button
              size="sm"
              variant="outline"
              onClick={() => rollup.mutate()}
              disabled={rollup.isPending}
            >
              {rollup.isPending ? "Bijwerken…" : "Maandrollup"}
            </Button>
          </div>
          <div className="h-80 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={yoy.data?.series ?? []}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Legend />
                {(yoy.data?.series?.[0]
                  ? Object.keys(yoy.data.series[0]).filter((k) => k !== "month")
                  : []
                ).map((year, i) => (
                  <Line
                    key={year}
                    type="monotone"
                    dataKey={year}
                    stroke={`var(--chart-${(i % 5) + 1})`}
                    dot={false}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Panel>
      </div>
    </main>
  );
}
